// Amount-first allocation section for the settle sheet's payment methods
// (GDN-2400, Option B). The amount leads and is always editable; the
// "Where does this money go?" preview below it is a live, tappable
// consequence — never a precondition. Over/under/unallocated amounts all
// resolve to an explicit, previewed outcome (partial payment / account
// credit); nothing hard-blocks.
//
// Pure presentational component: state and tracking live in SettleSheet.

// Direct file import (no feature barrel) — shared eligibility design tokens.
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import {
  Box,
  Checkbox,
  Chip,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';

import {
  type AllocationLine,
  type AllocationResult,
  type CopaySuggestion,
  formatCheckedAtTime,
} from '../utils/allocationEngine';

interface SettleAllocationSectionProps {
  amountStr: string;
  onAmountChange: (value: string) => void;
  allocation: AllocationResult;
  /** Eligibility-suggested copay driving the fill-the-amount chip. */
  copaySuggestion?: CopaySuggestion | null;
  onCopayChipClick: () => void;
  /** Tap a row to include/exclude it. `included` is the requested new state. */
  onToggleTarget: (targetId: string, included: boolean) => void;
}

export const SettleAllocationSection = ({
  amountStr,
  onAmountChange,
  allocation,
  copaySuggestion,
  onCopayChipClick,
  onToggleTarget,
}: SettleAllocationSectionProps) => (
  <Box>
    {/* 1 — Amount first, always editable */}
    <TextField
      label="Amount"
      value={amountStr}
      onChange={e => onAmountChange(e.target.value)}
      fullWidth
      size="small"
      autoFocus
      placeholder="0.00"
      slotProps={{
        input: {
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        },
      }}
      sx={{ mb: 1 }}
    />

    {/* 2 — Eligibility suggestion chip: tap to fill the amount */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
      {copaySuggestion ? (
        <>
          <Chip
            size="small"
            icon={<BoltOutlinedIcon sx={{ fontSize: 14 }} />}
            label={`Copay ${dollars(copaySuggestion.amountCents)} — suggested from coverage`}
            onClick={onCopayChipClick}
            sx={{
              bgcolor: ELIG_TOKENS.greenSoft,
              color: ELIG_TOKENS.green,
              fontWeight: 600,
              '& .MuiChip-icon': { color: ELIG_TOKENS.green },
              '&:hover': { bgcolor: ELIG_TOKENS.greenSoft, filter: 'brightness(0.96)' },
            }}
          />
          {(copaySuggestion.planName || copaySuggestion.checkedAt) && (
            <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
              {[
                copaySuggestion.planName,
                copaySuggestion.checkedAt
                  ? `checked ${formatCheckedAtTime(copaySuggestion.checkedAt)}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
        </>
      ) : (
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
          No suggested copay from coverage. Any amount still works.
        </Typography>
      )}
    </Box>

    {/* 3 — Live allocation preview */}
    <Typography
      variant="overline"
      sx={{ color: ELIG_TOKENS.midnight, fontWeight: 700, display: 'block', mb: 0.5 }}
    >
      Where does this money go?
    </Typography>
    <Box
      sx={{
        border: `1px solid ${ELIG_TOKENS.lineStrong}`,
        borderRadius: 1,
        overflow: 'hidden',
        mb: 2,
      }}
    >
      {allocation.lines.map(line => (
        <AllocationRow
          key={line.target.id}
          line={line}
          amountCents={allocation.amountCents}
          onToggle={() => onToggleTarget(line.target.id, !line.included)}
        />
      ))}
      <RemainderRow creditCents={allocation.creditCents} />
    </Box>
  </Box>
);

// ─── Allocation preview rows ─────────────────────────────────────────────────

const KIND_CHIP: Record<'copay' | 'charge', { label: string; color: string; bg: string }> = {
  copay: { label: 'Copay', color: ELIG_TOKENS.green, bg: ELIG_TOKENS.greenSoft },
  charge: { label: 'Charge', color: ELIG_TOKENS.gray, bg: ELIG_TOKENS.graySoft },
};

const AllocationRow = ({
  line,
  amountCents,
  onToggle,
}: {
  line: AllocationLine;
  amountCents: number;
  onToggle: () => void;
}) => {
  const { target, appliedCents, included } = line;
  const kindChip = KIND_CHIP[target.kind];

  const status = !included
    ? { text: 'Excluded — tap to include', color: ELIG_TOKENS.inkFaint }
    : appliedCents === 0
      ? amountCents === 0
        ? { text: 'Waiting for amount', color: ELIG_TOKENS.inkFaint }
        : { text: 'Nothing left to apply', color: ELIG_TOKENS.inkFaint }
      : appliedCents < target.dueCents
        ? {
            text: `Partial — ${dollars(appliedCents)} of ${dollars(target.dueCents)}`,
            color: ELIG_TOKENS.amber,
          }
        : { text: 'Paid in full', color: ELIG_TOKENS.green };

  return (
    <Box
      onClick={onToggle}
      role="button"
      aria-pressed={included}
      aria-label={`${included ? 'Exclude' : 'Include'} ${target.title}`}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
        gap: 1.25,
        alignItems: 'center',
        px: 2,
        py: 1.25,
        cursor: 'pointer',
        bgcolor: included ? ELIG_TOKENS.card : ELIG_TOKENS.bg,
        opacity: included ? 1 : 0.6,
        '&:not(:first-of-type)': { borderTop: `1px solid ${ELIG_TOKENS.line}` },
        '&:hover': { bgcolor: included ? ELIG_TOKENS.bg : ELIG_TOKENS.card },
      }}
    >
      <Checkbox
        checked={included}
        size="small"
        sx={{ p: 0, pointerEvents: 'none' }}
        tabIndex={-1}
      />
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 600 }}>
            {target.title}
          </Typography>
          <Chip
            size="small"
            label={kindChip.label}
            sx={{
              height: 18,
              fontSize: 11,
              fontWeight: 600,
              color: kindChip.color,
              bgcolor: kindChip.bg,
            }}
          />
          {target.suggestedFromCoverage && (
            <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
              from coverage
            </Typography>
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: ELIG_TOKENS.inkFaint,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {target.subtitle}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography
          variant="body2"
          sx={{ color: ELIG_TOKENS.ink, fontWeight: 700, whiteSpace: 'nowrap' }}
        >
          {included ? dollars(appliedCents) : '—'}
        </Typography>
        <Typography variant="caption" sx={{ color: status.color, whiteSpace: 'nowrap' }}>
          {status.text}
        </Typography>
      </Box>
    </Box>
  );
};

const RemainderRow = ({ creditCents }: { creditCents: number }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr) auto',
      gap: 1.25,
      alignItems: 'center',
      px: 2,
      py: 1.25,
      borderTop: `1px solid ${ELIG_TOKENS.lineStrong}`,
      bgcolor: creditCents > 0 ? ELIG_TOKENS.greenTint : ELIG_TOKENS.bg,
    }}
  >
    <SavingsOutlinedIcon
      sx={{ fontSize: 18, color: creditCents > 0 ? ELIG_TOKENS.green : ELIG_TOKENS.inkFaint }}
    />
    <Box>
      <Typography
        variant="body2"
        sx={{
          color: creditCents > 0 ? ELIG_TOKENS.green : ELIG_TOKENS.inkSoft,
          fontWeight: 600,
        }}
      >
        Remainder → account credit
      </Typography>
      <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
        Always allowed — applies to future visits automatically
      </Typography>
    </Box>
    <Typography
      variant="body2"
      sx={{
        color: creditCents > 0 ? ELIG_TOKENS.green : ELIG_TOKENS.inkFaint,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {dollars(creditCents)}
    </Typography>
  </Box>
);

const dollars = (cents: number): string => {
  const abs = Math.abs(cents);
  if (abs % 100 === 0) return `$${abs / 100}`;
  return `$${(abs / 100).toFixed(2)}`;
};
